'use client';

import { useTranslation } from 'react-i18next';
import { Card, Checkbox } from '@akademi/ui';
import { SlidersHorizontal } from 'lucide-react';
import { cardReset } from '../_shared/styles';

const FACETS_CSS = `
.mp-facet-group { border-top: 1px solid var(--color-border-subtle, rgba(0,0,0,0.08)); }
.mp-facet-group:first-of-type { border-top: 0; }
.mp-facet-summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3, 12px) 0;
  font-weight: 600;
  font-size: var(--font-size-sm, 13px);
  color: var(--color-text-primary, #18181b);
}
.mp-facet-summary::-webkit-details-marker { display: none; }
.mp-facet-summary::after {
  content: '';
  width: 8px; height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
  transition: transform 150ms ease;
  opacity: 0.5;
}
.mp-facet-group[open] > .mp-facet-summary::after { transform: rotate(45deg); }
.mp-facet-option {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 0;
  font-size: var(--font-size-sm, 13px);
  color: var(--color-text-secondary, #52525b);
  cursor: pointer;
}
`;

export interface MarketplaceFacetGroup {
  key: string;
  label: string;
  options: { value: string; label: string; count?: number }[];
}

export function MarketplaceFacets({
  groups,
  selected,
  onToggle,
}: {
  groups: MarketplaceFacetGroup[];
  selected: Record<string, Set<string>>;
  onToggle: (groupKey: string, value: string) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  if (groups.length === 0) return null;

  return (
    <Card
      style={{
        ...cardReset,
        padding: 'var(--space-4, 16px)',
        borderRadius: 'var(--radius-lg, 12px)',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-border-default, rgba(0,0,0,0.14))',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: FACETS_CSS }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 700,
          fontSize: 'var(--font-size-md, 15px)',
          paddingBottom: 'var(--space-2, 8px)',
        }}
      >
        <SlidersHorizontal size={16} />
        {t('marketplace.courseGrid.filters', { defaultValue: 'Filters' })}
      </div>
      {groups.map((g) => (
        <details key={g.key} className="mp-facet-group" open>
          <summary className="mp-facet-summary">{g.label}</summary>
          <div style={{ paddingBottom: 'var(--space-2, 8px)' }}>
            {g.options.map((o) => {
              const id = `mp-facet-${g.key}-${o.value}`;
              const checked = selected[g.key]?.has(o.value) ?? false;
              return (
                <label key={o.value} htmlFor={id} className="mp-facet-option">
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={() => onToggle(g.key, o.value)}
                  />
                  <span style={{ flex: 1 }}>{o.label}</span>
                  {typeof o.count === 'number' && (
                    <span style={{ color: 'var(--color-text-tertiary, #9ca3af)' }}>{o.count}</span>
                  )}
                </label>
              );
            })}
          </div>
        </details>
      ))}
    </Card>
  );
}
